import { TradingGridContainer, TradingGridItem, TradingLoadingButton, TradingTooltip } from '@trading-ui/core';
import TradingDatepicker from '@trading-ui/datepicker';
import { DownloadFileIcon } from '@trading-ui/icons';
import TradingSelect from '@trading-ui/select';
import { forwardRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useAppContext from '@/hooks/contexts/useAppContext';
import useAppStore from '@/store/app.store';

import { AppUtils } from '@/utils/app.utils';

import { ROLES } from '@/constants/roles';

import type { ConsumptionTableResponse, StylesObject } from '@/interfaces';

import useConsumerStore from '../../store/consumer.store';

type Props = {
  canLoadData: boolean;
  loadData: () => void;
  isLoading: boolean;
  exportData: () => void;
  isExporting: boolean;
  tableData: ConsumptionTableResponse | undefined;
  isSmallScreen: boolean;
};

const styles = {
  inputs: {
    '.MuiAutocomplete-inputRoot': { maxHeight: '30px' },
    '.MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 0 },
  },
} as StylesObject;

const Actionbar = forwardRef<HTMLDivElement, Props>(
  ({ canLoadData, loadData, isLoading, exportData, isExporting, tableData, isSmallScreen = false }, ref) => {
    const { t } = useTranslation();
    const { language } = useAppContext();

    const user = useAppStore((state) => state.user);
    const masters = useAppStore((state) => state.masters);
    const { dateRange, companies, cups, setDateRange, setCups, setCompaniesWithCupsCleanup } = useConsumerStore();

    const isAdmin = user?.role === ROLES.MOEVE_ADMIN;

    // Get company options based on role
    const companyOptions = useMemo(() => {
      if (isAdmin) {
        return AppUtils.formatCompaniesToSelectOptions(masters.companies);
      }
      // For non-admin users, use their assigned companies
      return (user?.companies ?? []).map((uc) => ({
        id: uc.companyId,
        label: uc.company?.name ?? '',
        value: uc.company?.name ?? '',
      }));
    }, [isAdmin, user?.companies, masters.companies]);

    const hasOnlyOneCompany = companyOptions.length === 1;

    // Auto-select company if user has only one
    useEffect(() => {
      if (hasOnlyOneCompany && companies.length === 0) {
        setCompaniesWithCupsCleanup([companyOptions[0]], masters.plants);
      }
    }, [hasOnlyOneCompany, companies.length, companyOptions, setCompaniesWithCupsCleanup, masters.plants]);

    // Handle companies change with automatic CUPS cleanup
    const handleCompaniesChange = (selectedCompanies: typeof companies) => {
      setCompaniesWithCupsCleanup(selectedCompanies, masters.plants);
    };

    // Get all available CUPS for selected companies
    const allCupsOptions = useMemo(
      () => AppUtils.filterCupsByCompanies(masters.plants, companies),
      [masters.plants, companies]
    );

    // Check if all selected cups have the same identifier (duplicates across companies)
    const { hasDuplicates, hasDistinct, selectedIdentifiers } = useMemo(() => {
      if (cups.length < 2) {
        return { hasDuplicates: false, hasDistinct: false, selectedIdentifiers: new Set<string>() };
      }

      const identifiers = cups.map((c) => c.identifier);
      const uniqueIdentifiers = new Set(identifiers);
      const identifiersSet = new Set(identifiers);

      // All cups have same identifier = duplicates mode
      const allSame = uniqueIdentifiers.size === 1;
      // Multiple different identifiers = distinct mode
      const multipleDifferent = uniqueIdentifiers.size > 1;

      return {
        hasDuplicates: allSame,
        hasDistinct: multipleDifferent,
        selectedIdentifiers: identifiersSet,
      };
    }, [cups]);

    // Filter CUPS options based on selection rules
    const filteredCupsOptions = useMemo(() => {
      const selectedCupsIds = new Set(cups.map((c) => c.id));

      // Less than 2 selected: show all options
      if (cups.length < 2) return allCupsOptions;

      // All selected have same identifier (duplicates): only show that identifier
      if (hasDuplicates) {
        const duplicateIdentifier = cups[0].identifier;
        return allCupsOptions.filter((cup) => cup.identifier === duplicateIdentifier || selectedCupsIds.has(cup.id));
      }

      // Multiple different identifiers selected: exclude any that would create duplicates
      if (hasDistinct) {
        return allCupsOptions.filter((cup) => !selectedIdentifiers.has(cup.identifier) || selectedCupsIds.has(cup.id));
      }

      return allCupsOptions;
    }, [allCupsOptions, cups, hasDuplicates, hasDistinct, selectedIdentifiers]);

    return (
      <div ref={ref}>
        <TradingGridContainer justifyContent="space-between" spacing={1}>
          <TradingGridItem xs={12} md={10} lg={9} xl={8}>
            <TradingGridContainer spacing={2}>
              <TradingGridItem xs={12} md={3}>
                <TradingDatepicker
                  label={t('consumer.filters.dateRangeLabel')}
                  fullWidth
                  onChange={(dates) => setDateRange(dates)}
                  startDate={dateRange?.[0] as Date}
                  endDate={dateRange?.[1] as Date}
                  isRange
                  locale={language === 'en' ? 'en-GB' : 'es'}
                />
              </TradingGridItem>
              <TradingGridItem xs={12} md={3}>
                <TradingSelect
                  label={t('consumer.filters.companyLabel')}
                  value={companies}
                  options={companyOptions}
                  onChange={handleCompaniesChange}
                  multiple
                  style={styles.inputs}
                  disabled={hasOnlyOneCompany}
                />
              </TradingGridItem>
              <TradingGridItem xs={12} md={3}>
                <TradingTooltip title={!companies.length ? t('consumer.filters.pleaseSelectCompanyFirst') : ''}>
                  <TradingSelect
                    label={t('consumer.filters.cupsLabel')}
                    value={cups}
                    options={filteredCupsOptions}
                    onChange={setCups}
                    multiple
                    disabled={!companies.length}
                    style={styles.inputs}
                  />
                </TradingTooltip>
              </TradingGridItem>
              <TradingGridItem xs={12} md={2}>
                <TradingLoadingButton
                  label={t('consumer.buttons.applyLabel')}
                  handleClick={loadData}
                  size="small"
                  disabled={!canLoadData}
                  loading={isLoading}
                  fullWidth={isSmallScreen}
                />
              </TradingGridItem>
            </TradingGridContainer>
          </TradingGridItem>
          <TradingGridItem xs={12} md={2} lg={3} xl={4} display="flex" justifyContent="flex-end">
            <TradingLoadingButton
              label={t('consumer.buttons.exportLabel')}
              endIcon={<DownloadFileIcon />}
              handleClick={exportData}
              loading={isExporting}
              disabled={!tableData?.consumptions.length}
              size="small"
              fullWidth={isSmallScreen}
            />
          </TradingGridItem>
        </TradingGridContainer>
      </div>
    );
  }
);

export default Actionbar;
